import {
  IconButton,
  Spacer,
  SvgChevronSmallDown,
  SvgChevronSmallUp,
  SvgClose,
  SvgSearch,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import xorWith from 'lodash/xorWith';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  toggleIsTranscriptSearchOpen,
  updateCurrentSearchIndex,
  updateTranscriptSearchInputValue,
  updateTranscriptSearchPhrase,
  updateTranscriptSearchPhraseIndexArray,
} from 'actions/shareVideo';
import {
  TRANSCRIPTION_SEARCH_CLOSED,
  TRANSCRIPTION_SEARCH_NAVIGATED,
} from 'constants/events';
import * as analytics from 'utilities/analytics';

import {
  selectCurrentSearchIndex,
  selectIsTranscriptSearchOpen,
  selectTranscriptPhrases,
  selectTranscriptSearchInputValue,
  selectTranscriptSearchPhrase,
  selectTranscriptSearchPhraseIndexArray,
  selectUserIsOwner,
} from 'selectors/shareVideo';
import { selectCurrentVideoId } from 'selectors/videoPlayer';

import TranscriptHeaderIcons, {
  SEARCH_TRANSCRIPT,
} from '../transcript-header-icons';
import { findIndicesOfSearchPhraseInPhrases } from '../../../../utilities/transcript/findSearchWordInTranscript';

import styles from './styles.module.less';

const isArrayEqual = (x, y) => isEmpty(xorWith(x, y, isEqual));

const TranscriptHeader = ({ onExit }) => {
  const dispatch = useDispatch();
  const searchTerm = useSelector(selectTranscriptSearchPhrase);
  const searchTermIndices = useSelector(selectTranscriptSearchPhraseIndexArray);
  const inputValue = useSelector(selectTranscriptSearchInputValue);
  const isTranscriptSearchOpen = useSelector(selectIsTranscriptSearchOpen);
  const textInput = useRef(null);
  const transcript = useSelector(selectTranscriptPhrases);
  const phrases = transcript?.phrases;
  const currentSearchIndex = useSelector(selectCurrentSearchIndex);
  const currentUserIsOwner = useSelector(selectUserIsOwner);
  const videoId = useSelector(selectCurrentVideoId);

  const storeUserSearchedPhrase = phrase => {
    dispatch(updateTranscriptSearchInputValue(phrase));

    const trimmedPhrase = phrase.trim();

    if (trimmedPhrase !== searchTerm) {
      dispatch(updateTranscriptSearchPhrase(trimmedPhrase));
    }
  };

  useEffect(() => {
    if (isTranscriptSearchOpen) {
      textInput.current.focus();
    }
  }, [isTranscriptSearchOpen]);

  useEffect(() => {
    if (phrases?.length > 0 && searchTerm.length > 0) {
      const indexArray = findIndicesOfSearchPhraseInPhrases(
        searchTerm,
        phrases
      );
      const isDifferentSearchIndicesArray = !isArrayEqual(
        indexArray,
        searchTermIndices
      );

      if (isDifferentSearchIndicesArray) {
        dispatch(updateTranscriptSearchPhraseIndexArray(indexArray));

        if (indexArray.length > 0) {
          dispatch(updateCurrentSearchIndex(1));
        } else {
          dispatch(updateCurrentSearchIndex(0));
        }
      }
    }
  }, [searchTerm, phrases, dispatch, searchTermIndices]);

  const incrementSearchIndex = () => {
    if (currentSearchIndex < searchTermIndices.length) {
      dispatch(updateCurrentSearchIndex(currentSearchIndex + 1));
    } else {
      dispatch(updateCurrentSearchIndex(1));
    }

    analytics.track(TRANSCRIPTION_SEARCH_NAVIGATED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const decrementSearchIndex = () => {
    if (currentSearchIndex > 1) {
      dispatch(updateCurrentSearchIndex(currentSearchIndex - 1));
    } else {
      dispatch(updateCurrentSearchIndex(searchTermIndices.length));
    }

    analytics.track(TRANSCRIPTION_SEARCH_NAVIGATED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  return (
    <>
      {!isTranscriptSearchOpen && (
        <div className={classNames(styles.header, styles.headerSearchNotOpen)}>
          <Text fontWeight="bold">Transcript Beta</Text>
          <div className="flex items:center">
            <TranscriptHeaderIcons />
            <IconButton
              altText="Close"
              size="small"
              icon={<SvgClose />}
              onClick={onExit}
            />
          </div>
        </div>
      )}
      {isTranscriptSearchOpen && (
        <div className={classNames(styles.header, styles.headerSearchOpen)}>
          <div className="flex grow:1">
            <TextInput
              size="small"
              placeholder={SEARCH_TRANSCRIPT}
              icon={<SvgSearch />}
              onChange={ev => storeUserSearchedPhrase(ev.target.value)}
              ref={textInput}
              onKeyDown={ev => {
                if (searchTermIndices.length > 0 && ev.which == 13) {
                  incrementSearchIndex();
                }
              }}
              value={inputValue}
            />
          </div>

          <div className="flex items:center justify:flexEnd ">
            {searchTerm.length > 0 && (
              <>
                <Spacer left={1}>
                  <div className={styles.monospaceText}>
                    <Text>
                      {currentSearchIndex}/{searchTermIndices.length}
                    </Text>
                  </div>
                </Spacer>
                <Spacer left={1}>
                  <div className="flex">
                    <IconButton
                      size="small"
                      altText="Previous Search Term"
                      icon={<SvgChevronSmallUp />}
                      isDisabled={currentSearchIndex === 0}
                      onClick={decrementSearchIndex}
                    />
                    <IconButton
                      size="small"
                      altText="Next Search Term"
                      icon={<SvgChevronSmallDown />}
                      isDisabled={searchTermIndices.length === 0}
                      onClick={incrementSearchIndex}
                    />
                  </div>
                </Spacer>
              </>
            )}
            <Spacer left={0.5}>
              <TextButton
                onClick={() => {
                  dispatch(toggleIsTranscriptSearchOpen(false));

                  analytics.track(TRANSCRIPTION_SEARCH_CLOSED, {
                    currentUserIsOwner,
                    video_id: videoId,
                  });
                }}
              >
                Cancel
              </TextButton>
            </Spacer>
          </div>
        </div>
      )}
    </>
  );
};

export default TranscriptHeader;
